import React from 'react';

import { UseFormReturn } from 'react-hook-form';

import { _email, CallCenterCreateDTO, SupportedTimezoneMap } from '_lib/api/apiTypes.shim';
import { AutocompleteFormItem } from '_lib/components/AutocompleteFormItem/AutocompleteFormItem';
import { CheckboxFormItem } from '_lib/components/CheckboxFormItem/CheckboxFormItem';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { FormHelperText } from '_lib/components/FormHelperText/FormHelperText';
import { Grid } from '_lib/components/Grid/Grid';
import { ExternalLink } from '_lib/components/Link/Link';
import { MenuItem } from '_lib/components/MenuItem/MenuItem';
import { SelectFormItem } from '_lib/components/SelectFormItem/SelectFormItem';
import { Stack } from '_lib/components/Stack/Stack';
import { PhoneNumberTextFieldFormItem, TextFieldFormItem } from '_lib/components/TextFieldFormItem/TextFieldFormItem';
import { useMediaQuery } from '_lib/hooks/useMediaQuery';

import { AuthUser } from '../auth/auth.types';
import { SystemUser } from '../auth/permissionHelpers';
import { BaseAddressForm } from '../FormItems/BaseAddressForm';

export type CallCenterFormValues = Pick<
  CallCenterCreateDTO,
  | 'name'
  | 'addressLine1'
  | 'addressLine2'
  | 'city'
  | 'stateOrProvince'
  | 'zipOrPostal'
  | 'country'
  | 'timezone'
  | 'phoneNumber'
  | 'faxNumber'
  | 'website'
  | 'email'
  | 'escalationEmails'
  | 'callDroppedEmails'
  | 'fromEmailAddress'
  | 'phoneCallWebhookEnabled'
>;

/**
 * Form is only responsible for fields it controls, and we can check for the generic constraint in the
 * props in order for this to be reusable.
 *
 * See https://github.com/orgs/react-hook-form/discussions/8311#discussioncomment-9759698
 */
type CallCenterFormProps<T extends CallCenterFormValues> = {
  callCenterFormContext: T extends CallCenterFormValues ? UseFormReturn<T> : never;
  authUser: AuthUser;
  isEditingExisting?: boolean;
};

export const CallCenterForm = <T extends CallCenterFormValues>(props: CallCenterFormProps<T>) => {
  const { callCenterFormContext, authUser, isEditingExisting } = props;

  const singleColumn = useMediaQuery((theme) => theme.breakpoints.only('xs'));

  const { control, setValue, clearErrors } = callCenterFormContext;

  return (
    <Stack width='100%' useFlexGap spacing={1}>
      <TextFieldFormItem name='name' label='Name' control={control} required fullWidth />
      <FlexBox>
        <Grid container spacing={1}>
          <BaseAddressForm
            formContext={callCenterFormContext}
            isNested={false}
            isEditingExisting={isEditingExisting || false}
          />
        </Grid>
      </FlexBox>
      <Stack direction={singleColumn ? 'column' : 'row'} spacing={1} useFlexGap width='100%'>
        <PhoneNumberTextFieldFormItem name='phoneNumber' label='Phone Number' control={control} fullWidth />
        <PhoneNumberTextFieldFormItem name='faxNumber' label='Fax Number' control={control} fullWidth />
      </Stack>
      <Stack direction={singleColumn ? 'column' : 'row'} spacing={1} useFlexGap width='100%'>
        <TextFieldFormItem
          name='website'
          label='Website'
          control={control}
          onChange={(e) => {
            if (e.target.value === '') {
              clearErrors('website');
              setValue('website', null);
            }
          }}
          fullWidth
        />
        <TextFieldFormItem name='email' label='Email' control={control} required fullWidth />
      </Stack>
      <Stack direction={singleColumn ? 'column' : 'row'} spacing={1} useFlexGap width='100%'>
        <SelectFormItem name='timezone' label='Timezone' control={control} required fullWidth>
          {Object.entries(SupportedTimezoneMap).map(([key, value]) => (
            <MenuItem key={key} value={key}>
              {value.displayName}
            </MenuItem>
          ))}
        </SelectFormItem>
        <Stack spacing={0} width='100%'>
          <TextFieldFormItem name='fromEmailAddress' label='Email From Address' control={control} required fullWidth />
          <FormHelperText sx={{ paddingLeft: 1.75, marginTop: -1 }}>
            Be sure to setup a{' '}
            {
              <ExternalLink variant='overline' href='https://support.google.com/a/answer/33786?hl=en'>
                SPF record
              </ExternalLink>
            }{' '}
            to use this feature properly.
          </FormHelperText>
        </Stack>
      </Stack>
      <AutocompleteFormItem
        control={control}
        name='escalationEmails'
        label='Escalation Emails'
        placeholder='Escalation Emails'
        fullWidth
        required
        inputMode='email'
        onChange={(_e, value) => {
          const validEmails = value.filter((email) => _email().safeParse(email).success);
          setValue('escalationEmails', validEmails as [string, ...string[]]);
        }}
      />
      <Stack spacing={0} width='100%'>
        <AutocompleteFormItem
          control={control}
          name='callDroppedEmails'
          label='Call Dropped Emails'
          placeholder='Call Dropped Emails'
          fullWidth
          inputMode='email'
          onChange={(_e, value) => {
            const validEmails = value?.filter((email) => _email().safeParse(email).success);
            setValue('callDroppedEmails', validEmails);
          }}
        />
        <FormHelperText sx={{ paddingLeft: 1.75, marginTop: -0.5 }}>
          Emails that receive a message for CPI Calls with &quot;Call Dropped&quot; outcome
        </FormHelperText>
      </Stack>
      {SystemUser({ user: authUser }) && (
        <CheckboxFormItem name='phoneCallWebhookEnabled' label='Enable Phone Call Webhook' control={control} />
      )}
    </Stack>
  );
};
